/**
 * Format: Campaign
 * -----------------------------------------------------------------------------
 * Formats response for querying nodes by IDs.
 */
import { formatProduct } from '~/format/format-product';
import { decodeApiId, transformEdges } from '~/helpers/graphql';

/**
 * Format products, variants and collections.
 * - Doesn't format products so variants can be excluded later.
 * - De-duplicates in campaign-product-list.vue.
 * - https://shopify.dev/docs/api/storefront/2023-10/objects/Metaobject
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
export function formatCampaign(response) {
  return response.nodes.filter(Boolean).flatMap((node) => {
    /**
     * Collection.
     */
    if (node.products) {
      return transformEdges(node.products);
    }

    /**
     * Variant.
     */
    if (node.product) {
      return {
        ...node.product,
        variantNodeId: node.id,
      };
    }

    /**
     * Product.
     */
    return node;
  });
}

/**
 * Format excluded collection product IDs.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
export function formatExcludedCollection(response) {
  return response.nodes
    .filter(Boolean)
    .flatMap((node) => {
      if (!node.products) {
        return false;
      }

      return transformEdges(node.products).map((product) => {
        return product.id;
      });
    })
    .filter(Boolean);
}

/**
 * Format campaign products from include and exclude lists.
 * - Filter out excluded items and ensure excluded variants aren't
 *   selected in the product card.
 * - If variant tile then exclude based on excluded variants or if the master
 *   product is in the excluded products.
 * @param {Array} include - Array of product and variant nodes.
 * @param {Object} exclude - Excluded product and variant IDs.
 * @param {Array} exclude.productIds - List of product GIDs.
 * @param {Array} exclude.variantIds - List of variant GIDs.
 * @returns {Array}
 */
export function formatCampaignProducts(include, exclude) {
  return include
    .filter((node) => {
      let excludeVariant = false;
      let excludeProduct = false;

      if (node.variantNodeId) {
        excludeVariant = exclude?.variantIds?.includes(node.variantNodeId);
      }

      excludeProduct = exclude?.productIds?.includes(node.id);

      return !excludeProduct && !excludeVariant;
    })
    .map((node) => {
      if (node.variantNodeId) {
        return formatProduct(node, {
          variantId: decodeApiId(node.variantNodeId),
        });
      }

      return formatProduct(node, { exclude: exclude?.variantIds });
    });
}
